// Client-side mirror of the server's pier shoreline snap (world.ts snapPier),
// extended with a player-chosen rotation: the build ghost previews EXACTLY the
// land-anchor + water-deck pair a click would place, and R / the ⟳ touch chip
// cycles through the legal alternatives. Owner: client.

import { DIRS } from '../shared/hex'
import { Terrain } from '../shared/terrain'
import { BUILDINGS, buildingFootprint } from '../shared/constants'
import type { GameState } from './state'
import { terrainAt } from './render/terrain'

export interface PierPlan {
  /** Land anchor hex (what the build message sends as q,r). */
  q: number
  r: number
  /** DIRS index from the anchor to the water deck. */
  dir: number
  /** How many placements the rotation control cycles through at this hover. */
  choices: number
}

/** A building (or site) already covers this hex — any footprint cell counts. */
function occupied(st: GameState, q: number, r: number): boolean {
  for (const b of st.buildings.values()) {
    for (const c of buildingFootprint(b.type, b.q, b.r, b.dir)) {
      if (c.q === q && c.r === r) return true
    }
  }
  return false
}

// The anchor must be terrain the pier can actually stand on — the server gates it on
// BUILDINGS.pier.on, NOT the looser TERRAIN.buildable (which lets Hills through). Using
// the same predicate keeps the green ghost honest so a click never silently fails.
// (Trees are fine: a treed grass hex is still Grass ∈ on, and the pier clears it.)
function buildableLand(st: GameState, q: number, r: number): boolean {
  const t = terrainAt(st, q, r)
  return t !== undefined && !!BUILDINGS.pier.on?.includes(t) && !occupied(st, q, r)
}

function openWater(st: GameState, q: number, r: number): boolean {
  return terrainAt(st, q, r) === Terrain.Water && !occupied(st, q, r)
}

/** DIRS indexes of the open shallow-water hexes beside a valid land anchor. */
export function pierWaterDirs(st: GameState, q: number, r: number): number[] {
  const out: number[] = []
  if (!buildableLand(st, q, r)) return out
  for (let i = 0; i < DIRS.length; i++) {
    if (openWater(st, q + DIRS[i].q, r + DIRS[i].r)) out.push(i)
  }
  return out
}

/**
 * Where a pier build-click at hover (q,r) would actually land; `rot` picks among
 * the alternatives. Hover on land: it is the anchor and rotation cycles its water
 * sides. Hover on water: that hex becomes the deck and rotation cycles the land
 * anchors around it. Null = no legal land+water pair here (the ghost turns red —
 * never a green preview of two water hexes).
 */
export function resolvePier(st: GameState, q: number, r: number, rot: number): PierPlan | null {
  const dirs = pierWaterDirs(st, q, r)
  if (dirs.length) {
    return { q, r, dir: dirs[((rot % dirs.length) + dirs.length) % dirs.length], choices: dirs.length }
  }
  if (openWater(st, q, r)) {
    const anchors: { q: number; r: number; dir: number }[] = []
    for (let i = 0; i < DIRS.length; i++) {
      const aq = q + DIRS[i].q
      const ar = r + DIRS[i].r
      // dir points from the land anchor BACK to the hovered water hex
      if (buildableLand(st, aq, ar)) anchors.push({ q: aq, r: ar, dir: (i + 3) % 6 })
    }
    if (anchors.length) {
      const a = anchors[((rot % anchors.length) + anchors.length) % anchors.length]
      return { q: a.q, r: a.r, dir: a.dir, choices: anchors.length }
    }
  }
  return null
}
